import React, { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, startOfDay, startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";
import EAButton from "../Buttons/EAButton";
import EASkeletonLoader from "../EASkeletonLoader";
import { createCalendarAvailability } from "./calendarAvailability";
import "./EADayCalendar.scss";

export interface EADayCalendarProps {
  selectedDate?: Date;
  onSelect: (date: Date) => void;
  /** Domyślnie początek dzisiaj. */
  fromDate?: Date;
  /** Brak górnej granicy, gdy niepodane. Sprzedaż kalendarzowa podaje własne +240 dni. */
  toDate?: Date;
  /** Dni spoza oferty – wyłączone, nie da się ich kliknąć. */
  disabledDates?: Date[];
  /** Wyprzedane – przekreślone, nadal klikalne. */
  soldOutDates?: Date[];
  /** Niedostępne na grafiku – wyszarzone, nadal klikalne. */
  unavailableDates?: Date[];
  /** false = daty z API jeszcze się ładują. */
  daysLoaded?: boolean;
  /** Czy użytkownik już wybrał dzień. Bez tego nie podświetlamy daty i nie przeskakujemy miesiąca. */
  hasSelection?: boolean;
}

const EADayCalendar: React.FC<EADayCalendarProps> = ({
  selectedDate,
  onSelect,
  fromDate,
  toDate,
  disabledDates,
  soldOutDates,
  unavailableDates,
  daysLoaded = true,
  hasSelection = true,
}) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const [month, setMonth] = useState<Date>(() => startOfMonth(selectedDate ?? new Date()));
  const initialMonthResolved = useRef(false);

  const fromTime = fromDate ? startOfDay(fromDate).getTime() : null;
  const toTime = toDate ? startOfDay(toDate).getTime() : null;

  const bounds = useMemo(() => {
    const from = fromTime === null ? startOfDay(new Date()) : new Date(fromTime);
    const to = toTime === null ? undefined : new Date(toTime);
    return { from, to, unavailableDates };
  }, [fromTime, toTime, unavailableDates]);

  const tracksAvailability = disabledDates !== undefined || soldOutDates !== undefined;
  const hasUnavailableDates = (unavailableDates?.length ?? 0) > 0;

  const availability = useMemo(
    () => createCalendarAvailability(disabledDates ?? [], soldOutDates ?? [], bounds),
    [disabledDates, soldOutDates, bounds]
  );

  // Skok na najbliższy miesiąc z terminem tylko zanim użytkownik cokolwiek wybierze.
  useEffect(() => {
    if (!daysLoaded || initialMonthResolved.current) return;
    initialMonthResolved.current = true;
    if (hasSelection || !tracksAvailability) return;
    const today = new Date();
    if (availability.monthHasAvailableDay(today)) return;
    const nearest = availability.findAvailableFrom(today);
    if (nearest) {
      setMonth(startOfMonth(nearest));
    }
  }, [daysLoaded, hasSelection, tracksAvailability, availability]);

  const nextAvailable = useMemo(
    () => (tracksAvailability ? availability.findAvailableFrom(startOfMonth(month)) : undefined),
    [availability, month, tracksAvailability]
  );
  const hasAnyAvailable = useMemo(
    () => (tracksAvailability ? availability.findAvailableFrom(bounds.from) !== undefined : false),
    [availability, bounds.from, tracksAvailability]
  );

  const modifiers = useMemo(() => {
    const next: Record<string, (date: Date) => boolean> = {
      available: (date: Date) => availability.isAvailable(date),
    };
    if (soldOutDates !== undefined) {
      next.soldOut = (date: Date) => availability.isSoldOut(date) && !availability.isDisabled(date);
    }
    if (hasUnavailableDates) {
      next.unavailable = (date: Date) =>
        availability.isUnavailable(date) && !availability.isSoldOut(date) && !availability.isDisabled(date);
    }
    return next;
  }, [availability, soldOutDates, hasUnavailableDates]);

  const monthIsEmpty = tracksAvailability && daysLoaded && !availability.monthHasAvailableDay(month);
  const showLegend = tracksAvailability || hasUnavailableDates;

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onSelect(date);
      return;
    }
    if (hasSelection && selectedDate) {
      onSelect(selectedDate);
    }
  };

  const handleGoToNearest = () => {
    if (nextAvailable) {
      setMonth(startOfMonth(nextAvailable));
    }
  };

  if (!daysLoaded) {
    return <EASkeletonLoader height="320px" />;
  }

  return (
    <div className="ea-day-calendar">
      <DayPicker
        mode="single"
        locale={locale}
        selected={hasSelection ? selectedDate : undefined}
        fromDate={bounds.from}
        toDate={bounds.to}
        month={month}
        onMonthChange={setMonth}
        onSelect={handleSelect}
        modifiers={modifiers}
        modifiersClassNames={{
          selected: "my-selected",
          today: "my-today",
          soldOut: "my-sold-out",
          available: "my-available",
          unavailable: "my-unavailable",
        }}
        disabled={availability.isDisabled}
      />

      {monthIsEmpty && (
        <div className="ea-day-calendar__empty" role="status">
          <p>{t("Brak dostępnych terminów w tym miesiącu")}</p>
          {nextAvailable ? (
            <EAButton
              onClick={handleGoToNearest}
              title={t("kalendarz_najblizszy_termin", {
                date: format(nextAvailable, "d MMMM", { locale }),
              })}
              noMargins
              variant="text"
            />
          ) : (
            <p className="ea-day-calendar__empty-hint">
              {hasAnyAvailable ? t("kalendarz_brak_dalszych_terminow") : t("kalendarz_brak_terminow_w_ofercie")}
            </p>
          )}
        </div>
      )}

      {showLegend && (
        <ul className="ea-day-calendar__legend">
          <li>
            <span className="ea-day-calendar__swatch ea-day-calendar__swatch--available" aria-hidden={true} />
            {t("kalendarz_legenda_wolne")}
          </li>
          <li>
            <span className="ea-day-calendar__swatch ea-day-calendar__swatch--selected" aria-hidden={true} />
            {t("kalendarz_legenda_wybrany")}
          </li>
          {soldOutDates !== undefined && (
            <li>
              <span className="ea-day-calendar__swatch ea-day-calendar__swatch--sold" aria-hidden={true} />
              {t("Wyprzedane dni")}
            </li>
          )}
          {hasUnavailableDates && (
            <li>
              <span className="ea-day-calendar__swatch ea-day-calendar__swatch--unavailable" aria-hidden={true} />
              {t("kalendarz_legenda_niedostepne")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default EADayCalendar;
